import DataQuery from "data/DataQuery";
import IRepository from "data/IRepository";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { DependencyList, useEffect, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import PromiseTrackerConfig from "features/shared/models/PromiseTrackerConfig";
import { DataLoadStatus } from "../enums/dataLoadStatus";
import ConditionalExecute from '../models/ConditionalExecute';

export const useQuery = <T>(
  query: DataQuery,
  initialData: T,
  deps: DependencyList = [],
  trackerConfig: PromiseTrackerConfig = { useTracker: true },
  conditionalExecute?: ConditionalExecute
): { data: T; loadStatus: DataLoadStatus; error: Error | null } => {
  
  // DI
  const repository = useInjection<IRepository<T>>(IocTypes.Repository);

  // state
  const [data, setData] = useState<any>(initialData);
  const [error, setError] = useState<Error | null>(null); // use for local error handling (on component)
  const [loadStatus, setLoadStatus] = useState(DataLoadStatus.BeforeFirstLoad); // indication if data was loaded for the first time

  // fetch data
  useEffect(() => {
    // fetch data only if conditional execute is defined and it condition f-ce is true
    // UC: if I want to load data later (typically after the condition is met)
    if (conditionalExecute !== undefined && conditionalExecute.apply) {
      if (conditionalExecute.condition()) {
        fetchAndSetData();   
      }
    } else {
      fetchAndSetData();
    }

    // return () => {};
  }, deps);

  const fetchAndSetData = async () => {
    try {
      let result: any;

      if (trackerConfig.useTracker) {
        result = await trackPromise<any>(getData(), trackerConfig.area);
      } else {
        result = await getData();
      }

      setData(result);
      setLoadStatus("AfterFirstLoad");
    } catch (error) {
      // TODO: přidat napojení errorů do reduxu (globální handling errorů) a zalogování do API
      setError(error);
      setLoadStatus("LoadError");
    }
  };

  const getData = () => {
    // array type
    if (Array.isArray(initialData)) {
      return repository.get(query);
    }
    // paged data result
    else if (isPagedDataObj(initialData)) {
      return repository.getPagedResult(query);
    }
    // other types
    else {
      return repository.getOne(query);
    }
  };

  const isPagedDataObj = (obj: T): boolean => {
    if (typeof obj === "object" && Object.keys(obj).length === 2) {
      return (
        (obj as any).hasOwnProperty("result") &&
        (obj as any).hasOwnProperty("count")
      );
    }

    return false;
  };

  return { data, loadStatus, error };
};
